import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import type { SimulationObjectiveChoice } from "@/types/sim-sandbox.types";
import type { SimulatableOrder } from "@opus-dt/shared-types";
import { OBJECTIVE_META, type ObjectiveMeta } from "./objective-meta";

interface Props {
  value: SimulationObjectiveChoice | null;
  onChange: (next: SimulationObjectiveChoice) => void;
  /** Ordens simuláveis da linha; só usadas pelo objetivo de prazo. */
  orders?: SimulatableOrder[];
  /** Objetivos a oferecer (o de prazo some em cenário de linha nova). */
  options?: readonly ObjectiveMeta[];
}

const DEFAULT_WINDOW_HOURS = 24;

// "o que você quer saber?". Cada objetivo pede UM dado, exibido junto da própria opção
// em vez de num painel separado: o período deixou de ser um passo (o objetivo o absorve).
export function ObjectivePicker({ value, onChange, orders = [], options }: Props) {
  function select(id: (typeof OBJECTIVE_META)[number]["id"]) {
    if (id === "TIME_TO_QUANTITY") {
      onChange({ kind: "TIME_TO_QUANTITY", quantity: Number.NaN });
      return;
    }
    if (id === "OP_ON_TIME") {
      onChange({ kind: "OP_ON_TIME", productionOrderId: null });
      return;
    }
    const window = {
      hours: value && "window" in value ? value.window.hours : DEFAULT_WINDOW_HOURS,
    };
    onChange(id === "TARGET_QUANTITY" ? { kind: "TARGET_QUANTITY", window } : { kind: id, window });
  }

  return (
    <div className="space-y-2">
      {(options ?? OBJECTIVE_META).map((o) => {
        const selected = value?.kind === o.id;
        return (
          <div
            key={o.id}
            className={
              selected
                ? "rounded-md border border-primary/60 bg-muted/30 p-3"
                : "rounded-md border p-3"
            }
          >
            <button
              type="button"
              className="flex w-full items-start gap-2.5 text-left"
              onClick={() => select(o.id)}
            >
              <input
                type="radio"
                name="sim-objective"
                className="mt-1 accent-brand-500"
                checked={selected}
                readOnly
                tabIndex={-1}
              />
              <span className="flex-1">
                <span className="block text-sm font-semibold">{o.question}</span>
                <span className="block text-[12px] leading-relaxed text-muted-foreground">
                  {o.hint}
                </span>
              </span>
            </button>

            {selected ? <ObjectiveField value={value} onChange={onChange} orders={orders} /> : null}
          </div>
        );
      })}
    </div>
  );
}

// O único dado que o objetivo escolhido exige. Campos numéricos nascem vazios (guardrail: nunca
// `0` literal, que obrigaria o usuário a apagar antes de digitar).
function ObjectiveField({
  value,
  onChange,
  orders,
}: {
  value: SimulationObjectiveChoice;
  onChange: (next: SimulationObjectiveChoice) => void;
  orders: SimulatableOrder[];
}) {
  if (value.kind === "OP_ON_TIME") {
    return <OrderField value={value} onChange={onChange} orders={orders} />;
  }
  if (value.kind === "TIME_TO_QUANTITY") {
    return (
      <div className="mt-2.5 space-y-1 pl-6">
        <Label htmlFor="obj-qty">Quantidade a produzir</Label>
        <Input
          id="obj-qty"
          type="number"
          min={1}
          step={1}
          placeholder="0"
          value={Number.isFinite(value.quantity) ? value.quantity : ""}
          onChange={(e) =>
            onChange({
              kind: "TIME_TO_QUANTITY",
              quantity: e.target.value === "" ? Number.NaN : e.target.valueAsNumber,
            })
          }
        />
      </div>
    );
  }

  return (
    <div className="mt-2.5 space-y-2 pl-6">
      <div className="space-y-1">
        <Label htmlFor="obj-window">Janela a avaliar (horas)</Label>
        <Input
          id="obj-window"
          type="number"
          min={1}
          max={720}
          step={1}
          placeholder="24"
          value={Number.isFinite(value.window.hours) ? value.window.hours : ""}
          onChange={(e) => {
            const hours = e.target.value === "" ? Number.NaN : e.target.valueAsNumber;
            onChange(
              value.kind === "TARGET_QUANTITY"
                ? { ...value, window: { hours } }
                : { kind: value.kind, window: { hours } }
            );
          }}
        />
      </div>
      {value.kind === "TARGET_QUANTITY" ? (
        <div className="space-y-1">
          <Label htmlFor="obj-min">Preciso de pelo menos (opcional)</Label>
          <Input
            id="obj-min"
            type="number"
            min={1}
            step={1}
            placeholder="0"
            value={value.minQuantity ?? ""}
            onChange={(e) =>
              onChange({
                ...value,
                minQuantity: e.target.value === "" ? undefined : e.target.valueAsNumber,
              })
            }
          />
        </div>
      ) : null}
    </div>
  );
}

// O gestor escolhe a ordem e não preenche mais nada. Ordens inelegíveis aparecem
// desabilitadas COM O MOTIVO: sumir sem dizer por quê faz o usuário procurá-la e concluir que a
// ordem desapareceu.
const REASON_LABEL: Record<string, string> = {
  NO_DUE_DATE: "sem prazo definido",
  NO_REMAINING: "quantidade já atingida",
  NO_PRODUCT: "sem produto principal",
};

function OrderField({
  value,
  onChange,
  orders,
}: {
  value: Extract<SimulationObjectiveChoice, { kind: "OP_ON_TIME" }>;
  onChange: (next: SimulationObjectiveChoice) => void;
  orders: SimulatableOrder[];
}) {
  if (orders.length === 0) {
    return (
      <p className="mt-2.5 pl-6 text-[12px] text-muted-foreground">
        Esta linha não tem Ordens de Produção agendadas ou em andamento.
      </p>
    );
  }

  return (
    <div className="mt-2.5 space-y-1 pl-6">
      <Label htmlFor="obj-order">Ordem de Produção</Label>
      <Select
        id="obj-order"
        value={value.productionOrderId ?? ""}
        onChange={(e) =>
          onChange({ kind: "OP_ON_TIME", productionOrderId: e.target.value || null })
        }
      >
        <option value="">Selecione…</option>
        {orders.map((o) => (
          <option key={o.id} value={o.id} disabled={!o.eligible}>
            {[
              o.code ?? "OP",
              o.productName,
              o.status === "RUNNING" && o.remainingQty != null
                ? `faltam ${o.remainingQty.toLocaleString("pt-BR")} un`
                : `${o.plannedQty.toLocaleString("pt-BR")} un`,
              o.reason ? `— ${REASON_LABEL[o.reason] ?? "indisponível"}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </option>
        ))}
      </Select>
      <p className="text-[11px] leading-relaxed text-muted-foreground">
        Quantidade, produto, janela e prazo vêm da ordem. Se ela já estiver em andamento, a conta é
        sobre o que <b>falta</b>, a partir de agora.
      </p>
    </div>
  );
}
